import { SetupIntent, StripeIbanElement } from '@stripe/stripe-js';
import { useStripe } from '@stripe/react-stripe-js';
import { useAuthentication } from '@context/businessContainer';
import { useGetClientSecret } from '@monofront/business/hooks';
import { useTranslations } from 'next-intl';

const useSetupIban = (): ((
  ibanHolder: string,
  ibanElement: StripeIbanElement,
) => Promise<SetupIntent | undefined>) => {
  const getClientSecret = useGetClientSecret();
  const stripe = useStripe();
  const { customer } = useAuthentication();
  const tErrors = useTranslations('Errors');

  return (
    ibanHolder: string,
    ibanElement?: StripeIbanElement,
  ): Promise<SetupIntent | undefined> =>
    new Promise((resolve, reject) => {
      if (!stripe || !ibanElement) {
        reject({
          errorKey: 'stripe_error',
          errorMessage: `${tErrors('operationFailed')} ${tErrors('tryAgain')}`,
        });

        return;
      }

      getClientSecret()
        .then((clientSecret) => {
          if (!customer?.email) {
            throw Error;
          }

          return stripe.confirmSepaDebitSetup(clientSecret!, {
            payment_method: {
              sepa_debit: ibanElement,
              billing_details: {
                name: ibanHolder,
                email: customer.email,
              },
            },
          });
        })
        .then(({ error, setupIntent }) => {
          if (
            error &&
            (error.message!.length > 150 ||
              error?.code === 'setup_intent_unexpected_state')
          ) {
            reject({
              errorKey: 'stripe_error',
              errorMessage: `${tErrors('operationFailed')} ${tErrors(
                'checkInformations',
              )}`,
            });

            return;
          } else if (error) {
            reject({
              errorKey: 'stripe_error',
              errorMessage: error.message!,
            });

            return;
          }

          resolve(setupIntent);
        })
        .catch(() => {
          reject({
            errorKey: 'unexpected_error',
            errorMessage: tErrors('unexpectedPaymentError'),
          });
        });
    });
};

export default useSetupIban;
